/**
 * Transpose Utilities
 * Story 4.4: Implement Transpose View
 *
 * Transforms flat grid data into a pivot-table-style layout where:
 * - Row field values become row headers
 * - Column field values become column headers
 * - Value fields are aggregated into cells
 */
import type { GridRow, GridColumn, TransposeConfig, TransposeAggregation } from '../types/gridTypes';
import { getTimePeriodKey, getTimePeriodLabel } from './timeSlicer';
import type { TimeSlicerConfig } from '../../../types/types/view';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Transposed row - a row in the pivot view
 */
export interface TransposedRow {
  id: string;
  rowValue: string;                    // Original row field value
  rowLabel: string;                    // Display label
  valueFieldId: string;                // Value field this row represents
  valueFieldLabel: string;             // Display label for the value field
  cells: Record<string, number | null>; // columnValue -> aggregated value
  total?: number | null;               // Row total if enabled
}

/**
 * Transposed column - dynamic columns from column field values
 */
export interface TransposedColumn {
  id: string;
  value: string;      // Original column field value
  label: string;      // Display label (may be time period label)
  sortOrder: number;  // For ordering columns
}

/**
 * Result of transpose transformation
 */
export interface TransposeResult {
  columns: TransposedColumn[];
  rows: TransposedRow[];
  columnTotals?: TransposedTotalsRow[]; // If showColumnTotals
}

/**
 * Column totals are calculated per value field to avoid mixing measures
 */
export interface TransposedTotalsRow {
  id: string;
  label: string;
  valueFieldId: string;
  cells: Record<string, number | null>;
}

// =============================================================================
// CORE TRANSPOSE FUNCTION
// =============================================================================

/**
 * Build transposed (pivot) data from flat grid rows
 *
 * Algorithm:
 * 1. Extract unique row values and column values
 * 2. For each row/column intersection, collect matching source rows
 * 3. Aggregate values using specified aggregation function
 * 4. Generate dynamic columns from column field values
 * 5. Optionally calculate row and column totals
 *
 * @param rows - Source data rows
 * @param columns - Column definitions (for type info)
 * @param config - Transpose configuration
 * @param timeSlicer - Optional time slicer config for column field
 * @returns Transposed data structure
 */
export function buildTransposedData(
  rows: GridRow[],
  columns: GridColumn[],
  config: TransposeConfig,
  timeSlicer?: TimeSlicerConfig
): TransposeResult {
  const { rowFieldId, columnFieldId, valueFields, showRowTotals = true, showColumnTotals = false } = config;
  const valueFieldMeta = valueFields.map(vf => {
    const columnDef = columns.find(c => c.id === vf.fieldId);
    return {
      ...vf,
      label: vf.label || columnDef?.label || vf.fieldId,
    };
  });
  const appendValueLabel = valueFieldMeta.length > 1 || valueFieldMeta.some(vf => vf.label !== (columns.find(c => c.id === vf.fieldId)?.label || vf.fieldId));

  // Filter to data rows only (exclude group rows)
  const dataRows = rows.filter(r => r.rowType !== 'group' && r.rowType !== 'subtotal' && r.rowType !== 'total');

  if (dataRows.length === 0) {
    return { columns: [], rows: [] };
  }

  // Get column type for time slicer support (AC-4.4.6)
  const columnDef = columns.find(c => c.id === columnFieldId);
  const isDateColumn = columnDef?.type === 'date';

  // Extract unique row values
  const rowValues = new Map<string, string>(); // value -> label
  for (const row of dataRows) {
    const value = row.values[rowFieldId];
    const key = String(value ?? '(empty)');
    if (!rowValues.has(key)) {
      rowValues.set(key, key);
    }
  }

  // Extract unique column values (with optional time slicer transform)
  const columnValues = new Map<string, { label: string; sortOrder: number }>(); // value -> {label, order}
  for (const row of dataRows) {
    let value = row.values[columnFieldId];
    let label: string;
    let sortOrder: number;

    // Apply time slicer if enabled for date columns (AC-4.4.6)
    if (isDateColumn && timeSlicer?.enabled) {
      const periodKey = getTimePeriodKey(
        value as Date | string | null,
        timeSlicer.period,
        timeSlicer.weekStartsOn ?? 0
      );
      label = getTimePeriodLabel(periodKey, timeSlicer.period, timeSlicer.weekStartsOn ?? 0);
      value = periodKey;
      // Sort by period key (chronological)
      sortOrder = periodKey ? parseInt(periodKey.replace(/\D/g, ''), 10) || 0 : 999999;
    } else {
      const key = String(value ?? '(empty)');
      label = key;
      sortOrder = 0; // Will be sorted alphabetically
    }

    const key = String(value ?? '(empty)');
    if (!columnValues.has(key)) {
      columnValues.set(key, { label, sortOrder });
    }
  }

  // Build column definitions for transposed grid
  const transposedColumns: TransposedColumn[] = Array.from(columnValues.entries())
    .map(([value, { label, sortOrder }], index) => ({
      id: `col-${value}`,
      value,
      label,
      sortOrder: sortOrder || index,
    }))
    .sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.label.localeCompare(b.label);
    });

  // Add total column if enabled
  if (showRowTotals) {
    transposedColumns.push({
      id: 'col-total',
      value: '__total__',
      label: 'Total',
      sortOrder: 999999,
    });
  }

  // Build data lookup: rowValue -> valueFieldId -> columnValue -> [source values]
  const dataMap = new Map<string, Map<string, Map<string, number[]>>>();

  for (const row of dataRows) {
    const rowKey = String(row.values[rowFieldId] ?? '(empty)');

    // Get column value (with time slicer transform if applicable)
    let colValue = row.values[columnFieldId];
    if (isDateColumn && timeSlicer?.enabled) {
      colValue = getTimePeriodKey(
        colValue as Date | string | null,
        timeSlicer.period,
        timeSlicer.weekStartsOn ?? 0
      );
    }
    const colKey = String(colValue ?? '(empty)');

    if (!dataMap.has(rowKey)) {
      dataMap.set(rowKey, new Map());
    }

    const rowValueMap = dataMap.get(rowKey)!;

    for (const valueField of valueFieldMeta) {
      if (!rowValueMap.has(valueField.fieldId)) {
        rowValueMap.set(valueField.fieldId, new Map());
      }

      const valueFieldMap = rowValueMap.get(valueField.fieldId)!;
      if (!valueFieldMap.has(colKey)) {
        valueFieldMap.set(colKey, []);
      }

      const cellValue = row.values[valueField.fieldId];
      if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
        const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
        if (!isNaN(numValue)) {
          valueFieldMap.get(colKey)!.push(numValue);
        }
      }
    }
  }

  // Build transposed rows
  const transposedRows: TransposedRow[] = [];

  for (const [rowKey, rowLabel] of rowValues.entries()) {
    for (const valueField of valueFieldMeta) {
      const cells: Record<string, number | null> = {};
      const rowDataByValue = dataMap.get(rowKey)?.get(valueField.fieldId);
      const aggregation = valueField.aggregation;
      let rowTotal = 0;
      let rowCount = 0;

      for (const col of transposedColumns) {
        if (col.value === '__total__') continue;

        const values = rowDataByValue?.get(col.value) || [];
        const aggregated = aggregateValues(values, aggregation);
        cells[col.id] = aggregated;

        if (aggregated !== null) {
          rowTotal += aggregated;
          rowCount++;
        }
      }

      // Calculate row total
      if (showRowTotals) {
        cells['col-total'] = aggregation === 'count'
          ? rowCount
          : aggregation === 'avg' && rowCount > 0
            ? rowTotal / rowCount
            : rowTotal;
      }

      const displayRowLabel = appendValueLabel ? `${rowLabel} - ${valueField.label}` : rowLabel;

      transposedRows.push({
        id: `row-${rowKey}-${valueField.fieldId}`,
        rowValue: rowKey,
        rowLabel: displayRowLabel,
        valueFieldId: valueField.fieldId,
        valueFieldLabel: valueField.label,
        cells,
        total: cells['col-total'],
      });
    }
  }

  // Calculate column totals if enabled
  let columnTotals: TransposedTotalsRow[] | undefined;
  if (showColumnTotals) {
    columnTotals = valueFieldMeta.map(valueField => {
      const cells: Record<string, number | null> = {};
      for (const col of transposedColumns) {
        if (col.value === '__total__') continue;

        const values: number[] = [];
        for (const rowKey of rowValues.keys()) {
          const colValues = dataMap.get(rowKey)?.get(valueField.fieldId)?.get(col.value) || [];
          values.push(...colValues);
        }
        cells[col.id] = aggregateValues(values, valueField.aggregation);
      }

      if (showRowTotals) {
        const totalsForValueField = transposedRows
          .filter(r => r.valueFieldId === valueField.fieldId)
          .map(r => r.total)
          .filter((v): v is number => v !== null);

        cells['col-total'] = aggregateValues(totalsForValueField, valueField.aggregation);
      }

      const isMultiValue = valueFieldMeta.length > 1;
      const label = isMultiValue ? `${valueField.label} Total` : 'Total';
      const id = isMultiValue ? `__totals_row__-${valueField.fieldId}` : '__totals_row__';

      return {
        id,
        label,
        valueFieldId: valueField.fieldId,
        cells,
      };
    });
  }

  return {
    columns: transposedColumns,
    rows: transposedRows,
    columnTotals,
  };
}

// =============================================================================
// AGGREGATION HELPERS
// =============================================================================

/**
 * Aggregate an array of values using specified aggregation function
 */
export function aggregateValues(
  values: number[],
  aggregation: TransposeAggregation
): number | null {
  if (values.length === 0) return null;

  switch (aggregation) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'count':
      return values.length;
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return null;
  }
}

/**
 * Get display label for aggregation mode
 */
export function getAggregationLabel(aggregation: TransposeAggregation): string {
  const labels: Record<TransposeAggregation, string> = {
    sum: 'Sum',
    count: 'Count',
    avg: 'Average',
    min: 'Min',
    max: 'Max',
  };
  return labels[aggregation] || aggregation;
}

// =============================================================================
// CONVERSION TO GRID FORMAT
// =============================================================================

/**
 * Convert TransposeResult to GridColumn[] and GridRow[] for display
 * This bridges the transpose data structure to the existing grid component
 */
export function transposeResultToGrid(
  result: TransposeResult,
  rowFieldLabel: string
): { columns: GridColumn[]; rows: GridRow[] } {
  // Create row header column
  const rowHeaderColumn: GridColumn = {
    id: '__row_header__',
    label: rowFieldLabel,
    type: 'text',
    isPrimary: true,
    readOnly: true,
  };

  // Create columns from transpose columns
  const dataColumns: GridColumn[] = result.columns.map(col => ({
    id: col.id,
    label: col.label,
    type: 'number',
    readOnly: true,
  }));

  const columns: GridColumn[] = [rowHeaderColumn, ...dataColumns];

  // Create rows from transpose rows
  const rows: GridRow[] = result.rows.map(row => ({
    id: row.id,
    rowType: 'data',
    values: {
      __row_header__: row.rowLabel,
      ...row.cells,
    },
  }));

  // Add totals row if available
  if (result.columnTotals?.length) {
    for (const totalRow of result.columnTotals) {
      rows.push({
        id: totalRow.id,
        rowType: 'total',
        values: {
          __row_header__: totalRow.label,
          ...totalRow.cells,
        },
      });
    }
  }

  return { columns, rows };
}
